import dynamic from 'next/dynamic'
import { useState } from 'react'
import styles from './FormFields.module.scss'

const ReCAPTCHA = dynamic(() => import('react-google-recaptcha'), { ssr: false })

export default function FormFields({ formFields, formState, handlers }) {
  const { fieldValues, fieldValuesArray, updateFieldValue, selectStates, dropdownRefs } = formState
  const { handleInputChange, handleSubfieldsInputChange, handleCaptcha, handleFileChange, handleOptionClick, toggleDropdown } = handlers
  const [loadCaptcha, setLoadCaptcha] = useState(false)

  return (
    <div className={styles.formFields} onFocusCapture={() => setLoadCaptcha(true)} onMouseEnter={() => setLoadCaptcha(true)}>
      {formFields.nodes.map((field, index) => {
        return (
          <div key={index} className={styles[field.type.toLowerCase()]}>
            {(() => {
              switch (field.type) {
                case 'TEXT':
                  return (
                    <div>
                      <label className={field.labelPlacement} htmlFor={field.databaseId}>
                        {field.label}
                      </label>
                      <br></br>
                      <input
                        type="text"
                        id={field.databaseId}
                        name={field.label}
                        placeholder={field.placeholder}
                        value={fieldValues[field.databaseId] || ''}
                        onChange={(event) => {
                          handleInputChange(index, event.target.value, field.databaseId, fieldValuesArray)
                          updateFieldValue(field.databaseId, event.target.value)
                        }}
                        data-cy={['Email', 'Goals', 'Name'].includes(field.label) ? `${field.label?.toLowerCase()}-input` : `text-input`}
                        autoComplete={
                          field.label === 'Email' || field.label === 'Email Address'
                            ? 'email'
                            : field.label === 'Name' || field.label === 'Full Name'
                              ? 'name'
                              : field.label === 'Phone Number' || field.label === 'Phone'
                                ? 'tel'
                                : 'off'
                        }
                      />
                    </div>
                  )
                case 'TEXTAREA':
                  return (
                    <div className={styles.textareaContainer}>
                      <label className={field.labelPlacement} htmlFor={field.databaseId}>
                        {field.label}
                      </label>
                      <br></br>
                      <textarea
                        rows="9"
                        className={styles.textarea}
                        id={field.databaseId}
                        name={field.label}
                        placeholder={field.placeholder}
                        value={fieldValues[field.databaseId] || ''}
                        onChange={(event) => {
                          handleInputChange(index, event.target.value, field.databaseId, fieldValuesArray)
                          updateFieldValue(field.databaseId, event.target.value)
                        }}
                        data-cy={['Goals'].includes(field.label) ? 'goals-input' : 'text-input'}
                      />
                    </div>
                  )

                case 'CAPTCHA':
                  return (
                    <div className={`captcha ${styles.centerRecaptcha}`}>
                      {loadCaptcha ? (
                        <ReCAPTCHA
                          style={{ display: 'flex', justifyContent: 'center' }}
                          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '6LeEjSoqAAAAAHnsbpREUKUroZZh6HXKlR28-44x'}
                          data-cy="recaptcha"
                          onChange={(value) => {
                            handleCaptcha(field.databaseId, value, fieldValuesArray)
                            updateFieldValue(field.databaseId, value)
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setLoadCaptcha(true)}
                          aria-label="Enable CAPTCHA verification"
                          style={{ padding: '10px 12px', cursor: 'pointer' }}
                        >
                          Load CAPTCHA
                        </button>
                      )}
                    </div>
                  )

                case 'FILEUPLOAD':
                  return (
                    <div className={styles.fileUploadContainer}>
                      <label htmlFor={field.databaseId}>{field.label}</label>
                      <input
                        id={field.databaseId}
                        type="file"
                        accept=".pdf"
                        style={{ border: 'none' }}
                        onChange={(e) => {
                          handleFileChange(index, e.target.files[0], field.databaseId, fieldValuesArray)
                          updateFieldValue(field.databaseId, e.target.files[0])
                        }}
                      />
                    </div>
                  )

                // In the SELECT case section, update the combobox div and options:

                case 'SELECT':
                  const listboxId = `listbox-${field.databaseId}`
                  return (
                    <div className={styles.customSelectContainer}>
                      <label className={field.labelPlacement} htmlFor={field.databaseId}>
                        {field.label}
                      </label>
                      <div
                        ref={(el) => (dropdownRefs.current[field.databaseId] = el)}
                        className={styles.customSelectDropdown}
                        onClick={() => toggleDropdown(field.databaseId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            toggleDropdown(field.databaseId)
                          }
                          if (e.key === 'Escape' && selectStates[field.databaseId]?.isOpen) {
                            toggleDropdown(field.databaseId)
                          }
                        }}
                        tabIndex={0}
                        role="combobox"
                        aria-controls={listboxId}
                        aria-expanded={selectStates[field.databaseId]?.isOpen || false}
                        aria-haspopup="listbox"
                        aria-owns={listboxId}
                        aria-label={field.label}
                      >
                        <div className={styles.selectedOption}>
                          {selectStates[field.databaseId]?.selectedOption ? (
                            <span id={field.databaseId}>{selectStates[field.databaseId].selectedOption}</span>
                          ) : (
                            <span id={field.databaseId} className={styles.placeholder}>
                              {field.placeholder}
                            </span>
                          )}
                        </div>
                        {selectStates[field.databaseId]?.isOpen && (
                          <ul id={listboxId} className={styles.customOptions} role="listbox" aria-label={`${field.label} options`}>
                            {field.choices?.map((option, index) => (
                              <li
                                key={option.value}
                                className={styles.customOption}
                                role="option"
                                aria-selected={selectStates[field.databaseId]?.selectedOption === option.value}
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleOptionClick(field.databaseId, option.value)
                                  handleInputChange(index, option.value, field.databaseId, fieldValuesArray)
                                  updateFieldValue(field.databaseId, option.value)
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleOptionClick(field.databaseId, option.value)
                                    handleInputChange(index, option.value, field.databaseId, fieldValuesArray)
                                    updateFieldValue(field.databaseId, option.value)
                                  }
                                }}
                              >
                                {option.text}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )

                case 'EMAIL':
                  return (
                    <div className={'emailfield'}>
                      <label className={field.labelPlacement} htmlFor={field.databaseId}>
                        {field.label}
                      </label>
                      <br></br>
                      <input
                        type="email"
                        id={field.databaseId}
                        placeholder={field.placeholder}
                        value={fieldValues[field.databaseId] || ''}
                        onChange={(event) => {
                          handleSubfieldsInputChange(index, event, field.databaseId, 'value', field.type)
                        }}
                        name={field.label}
                        autoComplete="email"
                        data-cy="email-input"
                      />
                    </div>
                  )

                case 'PHONE':
                  return (
                    <div>
                      <br />
                      <input
                        type="text"
                        id={field.databaseId}
                        placeholder={field.placeholder}
                        name={field.label}
                        autoComplete="tel"
                        value={fieldValues[field.databaseId] || ''}
                        onChange={(event) => {
                          const inputValue = event.target.value
                          const sanitizedValue = inputValue.replace(/[^0-9]/g, '').slice(0, 10) // Only take first 10 digits
                          handleInputChange(index, sanitizedValue, field.databaseId, fieldValuesArray)
                          updateFieldValue(field.databaseId, sanitizedValue)
                        }}
                        pattern="\d*"
                        inputMode="numeric"
                        data-cy="phone-input"
                      />
                    </div>
                  )
              }
            })()}
          </div>
        )
      })}
    </div>
  )
}
